import React, { ChangeEvent, FocusEvent, useEffect, useState } from "react";
import "../../Settings.css";
import "./VehicleMakeModel.css";
import { Table, Alert, Button } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Creatable from 'react-select/creatable';
import Axios from 'axios';
import AxiosConfig from '../../../../utilities/AxiosConfig';
import Loader from '../../../../components/common/Loader';

// interface Props {

// }

function VehicleMakeModel(/*props: Props*/) {
    const [showAddMakeModel, setShowAddMakeModel] = useState(false);
    const [currentRowEdit, setCurrentRowEdit] = useState<number>(-1);   // this holds the index of row being editted, so -1 means no rows are being editted
    const [loading, setLoading] = useState(false);
    const [tableRows, setTableRows] = useState([] as makeAndModelData[]);
    const [makesDropdownData, setMakesDropdownData] = useState([] as vehicleMake[]);
    const [selectedMake, setSelectedMake] = useState<vehicleMake>();
    const [newModel, setNewModel] = useState<string>();
    const [editedModel, setEditedModel] = useState<string>();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [searchString, setSearchString] = useState<string>();

    interface singleColumn {
        columnKey: string;
        columnName: string;
    };
    interface makeAndModelData {
        VehicleMakeDescription: string | null;
        VehicleModelDescription: string;
        VehicleMakeId: number | null;
        VehicleModelId: number;
    };
    interface vehicleMake {
        VehicleMakeDescription: string;
        VehicleMakeId: number;
    };
    interface selectOption {
        value: string;
        label: string;
    };

    const columns: singleColumn[] = [
        { columnKey: 'VehicleMakeDescription', columnName: 'Make' },
        { columnKey: 'VehicleModelDescription', columnName: 'Model' }
    ];

    const getVehicleMakesAndModels = () => {
        setLoading(true);

        Axios.get(`${process.env.REACT_APP_API_URL}/api/fleet/getVehicleMakesAndModels`, AxiosConfig.configValue())
        // Axios.get(`http://localhost:3001/api/fleet/getVehicleMakesAndModels`, AxiosConfig.configValue())
        .then(response => {
            if (!response.data.Error.success) {
                setErrorMessage('There was an error getting vehicle makes and models.');
                setShowErrorMessage(true);
                return;
            }

            const makesAndModelsArray: makeAndModelData[] = [];
            response.data.response.forEach((vehicleData: { VehicleMakeDescription: string | null, VehicleModelDescription: string, VehicleMakeId: number | null, VehicleModelId: number }) => {
                const tempMakeAndModelData: makeAndModelData = {
                    VehicleMakeDescription: vehicleData.VehicleMakeDescription,
                    VehicleModelDescription: vehicleData.VehicleModelDescription,
                    VehicleMakeId: vehicleData.VehicleMakeId,
                    VehicleModelId: vehicleData.VehicleModelId
                };
                makesAndModelsArray.push(tempMakeAndModelData);
            });
            setTableRows(makesAndModelsArray);
        }).catch(error => {
            setErrorMessage('There was an error getting vehicle makes and models.');
            setShowErrorMessage(true);
        }).finally(() => { setLoading(false); });
    };
    
    const getVehicleMakes = () => {
        Axios.get(`${process.env.REACT_APP_API_URL}/api/fleet/getVehicleMakes`, AxiosConfig.configValue())
        // Axios.get(`http://localhost:3001/api/fleet/getVehicleMakes`, AxiosConfig.configValue())
        .then(response => {
            if (!response.data.Error.success) {
                setErrorMessage('There was an error getting vehicle makes.');
                setShowErrorMessage(true);
                return;
            }

            setMakesDropdownData(response.data.response);
        }).catch(error => {
            setErrorMessage('There was an error getting vehicle makes.');
            setShowErrorMessage(true);
        }).finally(() => { });
    };

    const addVehicleMakeAndModel = (vehicle: makeAndModelData) => {
        Axios.post(`${process.env.REACT_APP_API_URL}/api/fleet/updateVehicleMakesAndModels`,
        // Axios.post(`http://localhost:3001/api/fleet/updateVehicleMakesAndModels`,
        vehicle,
        AxiosConfig.configValue('min'))
        .then(response => {
            if (!response.data.Error.success) {
                setErrorMessage('There was an error adding vehicle make and model.');
                setShowErrorMessage(true);
                return;
            }

            getVehicleMakesAndModels();
            if (vehicle.VehicleMakeId === 0) getVehicleMakes();
        }).catch(error => {
            setErrorMessage('There was an error adding vehicle make and model.');
            setShowErrorMessage(true);
        }).finally(() => { });
    };

    const editVehicleModel = (vehicle: makeAndModelData) => {
        Axios.post(`${process.env.REACT_APP_API_URL}/api/fleet/updateVehicleMakesAndModels`,
        // Axios.post(`http://localhost:3001/api/fleet/updateVehicleMakesAndModels`,
        vehicle,
        AxiosConfig.configValue('min'))
        .then(response => {
            if (!response.data.Error.success) {
                setErrorMessage('There was an error editing vehicle model.');
                setShowErrorMessage(true);
                return;
            }

            const vehicleIndex = tableRows.findIndex((existingMakeModel) => existingMakeModel.VehicleModelId === vehicle.VehicleModelId);
            tableRows[vehicleIndex] = vehicle;
            setTableRows([...tableRows]);
            setCurrentRowEdit(-1);
        }).catch(error => {
            setErrorMessage('There was an error editing vehicle model.');
            setShowErrorMessage(true);
        }).finally(() => { });
    };

    const renderTooltip = (text: string) => (
        <Tooltip id={`${text.replace(/\s+/g, '-')}-tooltip`}>
            {text}
        </Tooltip>
    );

    const renderMakeSelect = () => {
        const makeOptions: selectOption[] = [];
        makesDropdownData.forEach((make: vehicleMake) => {
            makeOptions.push({ value: make.VehicleMakeId.toString(), label: make.VehicleMakeDescription });
        });
        return (
            <Creatable options={makeOptions} placeholder='Make' onChange={(option: any) => handleMakeSelect(option)} />
        );
    };

    const handleMakeSelect = (selected: any) => {
        const isNew = selected?.__isNew__ ? selected.__isNew__ : false;
        const makeId = isNew ? 0 : Number(selected.value);

        setSelectedMake({ VehicleMakeDescription: selected.label, VehicleMakeId: makeId });
    };

    const handleSaveNewMakeAndModel = () => {
        if (!selectedMake || !newModel) return;        

        const newMakeAndModel: makeAndModelData = {
            VehicleMakeDescription: selectedMake.VehicleMakeDescription,
            VehicleMakeId: selectedMake.VehicleMakeId,
            VehicleModelDescription: newModel,
            VehicleModelId: 0
        };

        if (checkIfMakeModelExists(newMakeAndModel)) { setShowAddMakeModel(false); setSelectedMake(undefined); setNewModel(undefined); return; }

        addVehicleMakeAndModel(newMakeAndModel);
        setShowAddMakeModel(false);
        setSelectedMake(undefined);
        setNewModel(undefined);
    };

    const handleEditModel = (currentMakeModel: makeAndModelData) => {
        if (!editedModel) return;
        if (editedModel.trim().length === 0) return;
        if (editedModel.trim().toLowerCase() === currentMakeModel.VehicleModelDescription.trim().toLowerCase()) return;

        const modifiedModel: makeAndModelData = {
            VehicleMakeDescription: currentMakeModel.VehicleMakeDescription,
            VehicleMakeId: currentMakeModel.VehicleMakeId,
            VehicleModelDescription: editedModel,
            VehicleModelId: currentMakeModel.VehicleModelId
        };

        if (checkIfMakeModelExists(modifiedModel)) { setCurrentRowEdit(-1); setEditedModel(undefined); return; }

        editVehicleModel(modifiedModel);
        setEditedModel(undefined);
    };

    const handleSearchMakeModel = (row: makeAndModelData) => {
        if (!searchString || searchString.length === 0) return true;
        if (row.VehicleMakeDescription && row.VehicleMakeDescription.trim().toLowerCase().includes(searchString.trim().toLowerCase())) return true;
        if (row.VehicleModelDescription && row.VehicleModelDescription.trim().toLowerCase().includes(searchString.trim().toLowerCase())) return true;
        return false;
    };

    const handleClearSearch = () => {
        if (!searchString || searchString.length === 0) return;
        setSearchString('');
    };

    const checkIfMakeModelExists = (checkingMakeModel: makeAndModelData) => {
        if (checkingMakeModel.VehicleMakeId === 0) return false;

        const sameMake = tableRows.filter((row) => row.VehicleMakeId === checkingMakeModel.VehicleMakeId);
        const comboInArray = sameMake.find((model) => model.VehicleModelDescription.toLowerCase().trim() === checkingMakeModel.VehicleModelDescription.toLowerCase().trim());
        if (comboInArray) return true;
        
        return false;
    };

    const toggleRenderDisabledButton = (currentVehicle: makeAndModelData, newValue: string | undefined) => {
        if (currentVehicle.VehicleModelDescription.trim().toLowerCase() === newValue?.trim().toLowerCase()) {
            return "material-icons-outlined fleetMaintenanceButtonDisabled";
        }
        return "material-icons-outlined fleetMaintenanceButton";
    }

    useEffect(() => {
        getVehicleMakesAndModels();
        getVehicleMakes();
        //r-18-lint-id
        //eslint-disable-next-line
    }, []);
    
    return (
        <div className="VehicleMakeModel Settings main-content col-sm fleetMaintenanceWrapper">
            {loading && (
                <Loader title='Loading Fleet Maintenance Table' />
            )}
            {!loading && (
                <>
                    <div className='fleetMaintenanceHeaderWrapper'>
                        <h2 className='fleetMaintenanceHeader'>Fleet Maintenance</h2>
                        <div className='fleetMaintenanceSearchbarWrapper'>
                            <Button variant='secondary' className='fleetMaintenanceSearchButton' size='sm' onClick={() => handleClearSearch()}>
                                <span className="material-icons-outlined">{(searchString && searchString.length > 0) ? 'close' : 'search'}</span>
                            </Button>
                            <Form.Control value={searchString || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value)} />
                        </div>
                    </div>
                    <div className="fleetMaintenanceTableWrapper">
                        <Table striped bordered className='fleetMaintenanceTable'>
                            <thead>
                                <tr>
                                    {columns.map((col) => (
                                        <th key={`fleet-maintenance-table-header-${col.columnKey}`} className='fleetMaintenanceTableHeader'>{col.columnName}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tfoot className='fleetMaintenanceFooter'>
                                <tr>
                                    <td></td>
                                    <td className='fleetMaintenanceFooterCell'>
                                        <div />                       
                                        <div className='fleetMaintenanceCellButtonWrapper'>
                                            <OverlayTrigger overlay={renderTooltip('Add Make/Model')}>
                                                <span className="material-icons-outlined fleetMaintenanceButton" onClick={() => setShowAddMakeModel(true)}>add</span>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                            <tbody>
                                {tableRows.map((row, index) => (            
                                    handleSearchMakeModel(row) && (
                                        <tr className='fleetMaintenanceTableRow' key={`row-${index}`}>
                                            <td key={`vehicle-make-${index}`}>{row.VehicleMakeDescription}</td>
                                            {currentRowEdit === index && (
                                                <td className="fleetMaintenanceFooterCell" key={`vehicle-model-${index}`}>
                                                    <div key={`vehicle-model-input-wrapper-${index}`}>
                                                        {/* <Form.Control type='text' autoFocus defaultValue={row.VehicleModelDescription} onBlur={() => setCurrentRowEdit(-1)} ></Form.Control> */}
                                                        <Form.Control key={`vehicle-model-input-${index}`} type='text' autoFocus onFocus={(event: FocusEvent<HTMLInputElement>) => setEditedModel(event.target.value)} defaultValue={row.VehicleModelDescription} onChange={(event: ChangeEvent<HTMLInputElement>) => setEditedModel(event.target.value)} maxLength={200}></Form.Control>
                                                    </div>
                                                    <div className='fleetMaintenanceCellButtonWrapper' key={`model-cell-buttons-wrapper-${index}`}>
                                                        <OverlayTrigger overlay={renderTooltip('Cancel')} key={`model-cell-overlay-trigger-clear-${index}`}>
                                                            <span key={`model-clear-${index}`} className="material-icons-outlined fleetMaintenanceButton" onClick={() => { setCurrentRowEdit(-1); setEditedModel(undefined); }}>clear</span>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger overlay={renderTooltip('Save')} key={`model-cell-overlay-trigger-save-${index}`}>
                                                            <span key={`model-save-${index}`} className={toggleRenderDisabledButton(row, editedModel)} onClick={() => handleEditModel(row)}>save</span>
                                                        </OverlayTrigger>
                                                    </div>                                    
                                                </td>
                                            )}
                                            {currentRowEdit !== index && (
                                                <td key={`model-display-text-${index}`} onClick={() => setCurrentRowEdit(index)}>{row.VehicleModelDescription}</td>
                                            )}
                                        </tr>
                                    )                                    
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    {showAddMakeModel && (
                        <div className='fleetMaintenanceAddMakeModelWrapper'>
                            <div className='fleetMaintenanceAddMakeModel'>
                                {renderMakeSelect()}
                            </div>
                            <div className='fleetMaintenanceAddMakeModel fleetMaintenanceFooterCell'>
                                <div>
                                    <Form.Control placeholder='Model' onChange={(event: ChangeEvent<HTMLInputElement>) => setNewModel(event.target.value)} maxLength={200}></Form.Control>
                                </div>
                                <div className='fleetMaintenanceCellButtonWrapper'>
                                    <OverlayTrigger overlay={renderTooltip('Cancel')}>
                                        <span className="material-icons-outlined fleetMaintenanceButton" onClick={() => { setShowAddMakeModel(false); setSelectedMake(undefined); setNewModel(undefined); }}>clear</span>
                                    </OverlayTrigger>
                                    <OverlayTrigger overlay={renderTooltip('Save')}>
                                            <span className={(selectedMake && newModel) ? "material-icons-outlined fleetMaintenanceButton" : "material-icons-outlined fleetMaintenanceButtonDisabled"} onClick={() => { if (selectedMake && newModel) handleSaveNewMakeAndModel() }}>save</span>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>                        
                    )}
                    <Alert className='fleetMaintenanceAlertContainer' variant='danger' show={showErrorMessage} onClose={() => { setShowErrorMessage(false); setErrorMessage(''); }} dismissible>
                        <p>{errorMessage}</p>
                    </Alert>
                </>
            )}
        </div>
    );
}

export default VehicleMakeModel;